import { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { appointmentService } from "../../services/appointmentService";
import { financeService } from "../../services/financeService";
import { notificationService } from "../../services/notificationService";
import { staffService } from "../../services/staffService";
import { serviceService } from "../../services/serviceService";
import { customerService } from "../../services/customerService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import AsyncCustomerSelect from "../../components/Common/AsyncCustomerSelect";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import PaymentCollectModal from "../../components/finance/PaymentCollectModal";
import { useToast } from "../../context/ToastContext";

// --- Takvim yapılandırması (09:00–19:00, saat başı 80px) ---
const START_HOUR = 9;
const END_HOUR = 20;
const HOUR_PX = 80;
const SNAP_MIN = 30; // sürükle-bırak / tıklama snap
const GRID_HEIGHT = (END_HOUR - START_HOUR) * HOUR_PX;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
const DAY_LABELS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const STAFF_DOTS = ["bg-primary", "bg-secondary", "bg-tier-gold", "bg-success", "bg-blue-500", "bg-purple-500"];

// card arka planları saydam tint (renk/10) — alttaki tema yüzeyi görünür,
// böylece hem açık hem koyu temada metin (on-surface) okunur kalır.
const STATUS_STYLES = {
  SCHEDULED: { card: "bg-blue-500/10 border-blue-500", dot: "bg-blue-500", badge: "bg-blue-500/10 text-blue-500", label: "Planlandı" },
  COMPLETED: { card: "bg-success/10 border-success", dot: "bg-success", badge: "bg-success/10 text-success", label: "Tamamlandı" },
  CANCELLED: { card: "bg-error/10 border-error", dot: "bg-error", badge: "bg-error/10 text-error", label: "İptal" },
};

const fmtTime = (t) => (t ? t.slice(0, 5) : "");
const toMin = (t) => { const [h, m] = fmtTime(t).split(":").map(Number); return h * 60 + m; };
const minToTime = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
const fmtMoney = (v) => (v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "—");
const toISO = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const initials = (name) => (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
const rangesOverlap = (s1, e1, s2, e2) => s1 < e2 && s2 < e1;

// Telefonu uluslararası (wa.me) biçimine çevir: sadece rakam, TR için 90 ön eki
const normalizePhone = (raw) => {
  let d = (raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("0")) d = d.slice(1);        // 0532... -> 532...
  if (d.length === 10) d = "90" + d;             // 532... -> 90532...
  else if (d.startsWith("90")) { /* zaten ülke kodlu */ }
  return d;
};

// WhatsApp marka ikonu (Material Symbols'da yok)
function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} width="1em" height="1em" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488" />
    </svg>
  );
}

function mondayOf(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }

const emptyForm = {
  customerId: "", customerLabel: "", staffId: "", serviceIds: [],
  appointmentDate: "", startTime: "", endTime: "", status: "SCHEDULED", notes: "",
};

export default function AppointmentsPage() {
  const { showToast } = useToast();
  useSelector((state) => state.auth.user);
  const [paymentRow, setPaymentRow] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // WhatsApp ile randevu saati hatırlatması: müşterinin kayıtlı numarasına hazır metinle WhatsApp açılır
  const sendWhatsAppReminder = (a) => {
    const phone = normalizePhone(a.customerPhone);
    if (!phone) { showToast("Müşterinin kayıtlı telefonu yok.", "error"); return; }
    const dateStr = new Date(a.appointmentDate).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });
    const svc = a.services?.map((l) => l.name).join(", ");
    const msg =
      `Sayın ${a.customerName},\n` +
      `${dateStr} tarihinde saat ${fmtTime(a.startTime)} randevunuzu hatırlatmak isteriz.` +
      (svc ? `\nHizmet: ${svc}` : "") +
      `\nSizi aramızda görmekten mutluluk duyarız. 💇`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  // SMS ile randevu saati hatırlatması: backend (mock/twilio) müşterinin kayıtlı numarasına gönderir
  const [smsSendingId, setSmsSendingId] = useState(null);
  const sendSmsReminder = async (a) => {
    setSmsSendingId(a.id);
    try {
      await notificationService.smsReminder(a.id);
      showToast(`${a.customerName} için hatırlatma SMS'i gönderildi.`, "success");
    } catch (err) {
      showToast(err.response?.data?.error ?? "SMS gönderilemedi.", "error");
    } finally {
      setSmsSendingId(null);
    }
  };

  // Randevudan doğrudan tahsilat: durumdan bağımsız, tam o randevunun kalanı üzerinden.
  const goToPayment = async (a) => {
    try {
      const res = await financeService.paymentStatus(a.id);
      const s = res.data?.data ?? {};
      if (Number(s.remaining) <= 0) {
        showToast("Bu randevunun açık bakiyesi yok.", "info");
        return;
      }
      setPaymentRow({
        appointmentId: a.id,
        customerName: a.customerName,
        total: s.total,
        remaining: s.remaining,
      });
      setSelected(null);
    } catch (err) {
      showToast(err.response?.data?.error ?? "Ödeme bilgisi alınamadı.", "error");
    }
  };

  const [viewMode, setViewMode] = useState("calendar"); // 'calendar' | 'list'
  const [scale, setScale] = useState("week"); // 'day' | 'week' | 'month'
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [staffFilter, setStaffFilter] = useState(null);
  const [serviceFilter, setServiceFilter] = useState(null);

  const toggleServiceFilter = (id) => {
    setServiceFilter((prev) => {
      const base = prev ?? new Set();
      const next = new Set(base);
      next.has(id) ? next.delete(id) : next.add(id);
      return next.size === 0 ? null : next;
    });
  };

  const isServiceFilterChecked = (id) => (serviceFilter ? serviceFilter.has(id) : false);

  const clearServiceFilter = () => setServiceFilter(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editOrigin, setEditOrigin] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [dragId, setDragId] = useState(null);

  const todayISO = toISO(new Date());

  // --- Aralık hesaplama (görünüme göre) ---
  const range = useMemo(() => {
    if (scale === "day") return { from: new Date(anchor), to: new Date(anchor) };
    if (scale === "week") { const s = mondayOf(anchor); return { from: s, to: addDays(s, 6) }; }
    // month: ay ızgarasını dolduracak şekilde pzt..paz
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: mondayOf(first), to: addDays(mondayOf(last), 6) };
  }, [scale, anchor]);

  const gridDays = useMemo(() => {
    const days = [];
    let d = new Date(range.from);
    while (d <= range.to) { days.push(new Date(d)); d = addDays(d, 1); }
    return days;
  }, [range]);

  const timeDays = scale === "day" ? [new Date(anchor)] : gridDays; // week=7 gün

  const loadCalendar = useCallback(() => {
    setLoading(true);
    appointmentService
      .calendar(toISO(range.from), toISO(range.to))
      .then((res) => setAppointments(res.data?.data ?? []))
      .catch(() => showToast("Randevular yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  }, [range, showToast]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  /**
   * Detay kartı, tıklama anındaki randevu nesnesinin kopyasını tutuyor. Takvim
   * yenilenince (ör. düzenleme sonrası) bu kopya güncellenmezse kart eski veriyi
   * göstermeye devam eder: durum rozeti, ödenen/kalan tutar ve hizmet listesi
   * bayat kalır. Örneğin tamamlanmış randevuya hizmet eklenince backend durumu
   * "Planlandı"ya çevirir ama kart hâlâ yeşil "Tamamlandı" gösterirdi.
   * Bu yüzden açık kart, taze listeden kimliğine göre yeniden bağlanır.
   */
  useEffect(() => {
    setSelected((prev) => {
      if (!prev) return prev;
      const fresh = appointments.find((a) => a.id === prev.id);
      // Randevu artık listede yoksa (silindi / aralık dışına taşındı) kartı kapat.
      return fresh ?? null;
    });
  }, [appointments]);

  useEffect(() => {
    staffService.list({ size: 500, active: true }).then((r) => setStaff(r.data?.data?.content ?? []));
    serviceService.list({ size: 500, active: true }).then((r) => setServices(r.data?.data?.content ?? []));
  }, []);

  const staffColor = useCallback((id) => {
    const idx = staff.findIndex((s) => s.id === id);
    return STAFF_DOTS[idx % STAFF_DOTS.length] || "bg-primary";
  }, [staff]);

  const servicesByCategory = useMemo(() => {
    const map = {};
    services.forEach((s) => {
      const cat = s.category || "Genel Hizmetler";
      if (!map[cat]) map[cat] = [];
      map[cat].push(s);
    });
    return map;
  }, [services]);

  const visibleAppointments = useMemo(() => appointments.filter((a) => {
    if (staffFilter && !staffFilter.has(a.staffId)) return false;
    if (serviceFilter) {
      if (!a.services?.some((l) => serviceFilter.has(l.serviceId))) return false;
    }
    return true;
  }), [appointments, staffFilter, serviceFilter]);

  const apptsByDay = useCallback((iso) => visibleAppointments.filter((a) => a.appointmentDate === iso), [visibleAppointments]);

  const cardGeometry = (a) => {
    const minsFromStart = toMin(a.startTime) - START_HOUR * 60;
    const top = Math.max(0, (minsFromStart / 60) * HOUR_PX);
    const height = Math.max(((a.durationMinutes || 60) / 60) * HOUR_PX - 6, 34);
    return { top, height };
  };

  const toggleStaff = (id) => {
    setStaffFilter((prev) => {
      const base = prev ?? new Set(staff.map((s) => s.id));
      const next = new Set(base);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const isStaffChecked = (id) => (staffFilter ? staffFilter.has(id) : true);

  // --- modal ---
  const openCreate = (preset = {}) => {
    setEditingId(null);
    setEditOrigin(null);
    setForm({ ...emptyForm, appointmentDate: preset.date || todayISO, startTime: preset.start || "", endTime: preset.end || "" });
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (a) => {
    setEditingId(a.id);
    // Düzenlemeye girerken ilk hali saklanır: tamamlanmış randevuya yeni hizmet
    // eklenirse backend durumu "Planlandı"ya çevirdiği için kullanıcı bunu
    // kaydetmeden önce görsün.
    setEditOrigin({ status: a.status, serviceIds: a.services?.map((l) => l.serviceId) ?? [] });
    setForm({
      customerId: a.customerId, customerLabel: a.customerName, staffId: a.staffId,
      serviceIds: a.services?.map((l) => l.serviceId) ?? [],
      appointmentDate: a.appointmentDate, startTime: fmtTime(a.startTime), endTime: fmtTime(a.endTime),
      status: a.status || "SCHEDULED", notes: a.notes ?? "",
    });
    setFormError(null);
    setSelected(null);
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditingId(null); };

  const toggleService = (id) => setForm((f) => ({
    ...f,
    serviceIds: f.serviceIds.includes(id) ? f.serviceIds.filter((x) => x !== id) : [...f.serviceIds, id],
  }));

  const selectedDuration = useMemo(() => form.serviceIds.reduce((sum, id) => sum + (services.find((x) => x.id === id)?.durationMinutes || 60), 0), [form.serviceIds, services]);
  const selectedTotal = useMemo(() => form.serviceIds.reduce((sum, id) => sum + Number(services.find((x) => x.id === id)?.price || 0), 0), [form.serviceIds, services]);
  /**
   * Tamamlanmış randevuya yeni hizmet eklenirse backend durumu "Planlandı"ya
   * çevirir (yeni hizmetin tahsilatı gözden kaçmasın diye). Kaydetmeden önce
   * uyarmak için aynı koşul burada da hesaplanır.
   */
  const willRevertToScheduled = useMemo(
    () =>
      editOrigin?.status === "COMPLETED" &&
      form.status === "COMPLETED" &&
      form.serviceIds.some((id) => !editOrigin.serviceIds.includes(id)),
    [editOrigin, form.status, form.serviceIds]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.staffId) { setFormError("Personel seçilmeli."); return; }
    if (form.serviceIds.length === 0) { setFormError("En az bir hizmet seçilmeli."); return; }
    setSaving(true);

    let targetCustomerId = form.customerId || null;
    if (!targetCustomerId && form.customerLabel?.trim()) {
      try {
        const newCustomerRes = await customerService.create({
          fullName: form.customerLabel.trim(),
        });
        targetCustomerId = newCustomerRes.data?.data?.id || null;
      } catch (err) {
        setFormError(
          "Müşteri kaydedilemedi: " +
            (err.response?.data?.error ?? err.message ?? "Bilinmeyen hata")
        );
        setSaving(false);
        return;
      }
    }

    const norm = (t) => (t && t.length === 5 ? `${t}:00` : t || null);
    const payload = {
      customerId: targetCustomerId,
      staffId: form.staffId,
      serviceIds: form.serviceIds,
      appointmentDate: form.appointmentDate,
      startTime: norm(form.startTime),
      endTime: norm(form.endTime),
      status: form.status,
      notes: form.notes.trim() || null,
    };
    try {
      if (editingId) { await appointmentService.update(editingId, payload); showToast("Randevu güncellendi.", "success"); }
      else { await appointmentService.create(payload); showToast("Randevu oluşturuldu.", "success"); }
      closeModal();
      loadCalendar();
    } catch (err) {
      setFormError(err.response?.data?.error ?? "İşlem başarısız.");
    } finally { setSaving(false); }
  };

  const changeStatus = async (a, status) => {
    try {
      await appointmentService.setStatus(a.id, status);
      showToast("Durum güncellendi.", "success");
      setSelected(null); loadCalendar();
    } catch (err) { showToast(err.response?.data?.error ?? "İşlem başarısız.", "error"); }
  };

  /** Tahsilatı yapılmış randevu silinemez/iptal edilemez (kasa tutarlılığı). */
  const isPaid = (a) => Number(a?.paidAmount ?? 0) > 0;
  const paidLockTitle = "Tahsilat yapılmış randevu için kullanılamaz. Önce kasadaki tahsilat kaydını iptal edin.";

  const triggerDelete = (a) => {
    setConfirmModal({
      isOpen: true, title: "Randevuyu Sil",
      message: `${a.customerName} için ${a.appointmentDate} randevusu silinecek. Emin misiniz?`,
      type: "danger",
      onConfirm: () => appointmentService.remove(a.id)
        .then(() => { showToast("Randevu silindi.", "success"); setSelected(null); loadCalendar(); })
        .catch((err) => showToast(err.response?.data?.error ?? "İşlem başarısız.", "error")),
    });
  };

  // --- Sürükle-bırak ---
  const dropToSlot = (e, iso) => {
    e.preventDefault();
    const appt = appointments.find((a) => a.id === dragId);
    setDragId(null);
    if (!appt) return;
    if (isPaid(appt)) {
      showToast("Tahsilat yapıldığı için bu randevu taşınamaz.", "error");
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const dur = appt.durationMinutes || 60;
    let startMin = START_HOUR * 60 + Math.round(((y / HOUR_PX) * 60) / SNAP_MIN) * SNAP_MIN;
    // ızgara sınırları içinde tut
    startMin = Math.max(START_HOUR * 60, Math.min(startMin, END_HOUR * 60 - dur));
    const endMin = startMin + dur;

    if (iso === appt.appointmentDate && startMin === toMin(appt.startTime)) return; // değişmedi

    // Dolu mu? Aynı personelin çakışan başka randevusu
    const clash = appointments.some((a) =>
      a.id !== appt.id && a.staffId === appt.staffId && a.appointmentDate === iso &&
      rangesOverlap(startMin, endMin, toMin(a.startTime), toMin(a.endTime))
    );
    if (clash) {
      showToast(`Bu alan dolu — ${appt.staffName} ${minToTime(startMin)}'de başka randevuda.`, "error");
      return;
    }

    const dateLabel = new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
    setConfirmModal({
      isOpen: true, title: "Randevuyu Taşı", type: "info",
      message: `${appt.customerName} randevusu ${dateLabel} ${minToTime(startMin)} - ${minToTime(endMin)} saatine taşınsın mı?`,
      onConfirm: () => performMove(appt, iso, startMin, endMin),
    });
  };

  const performMove = async (appt, iso, startMin, endMin) => {
    try {
      await appointmentService.update(appt.id, {
        customerId: appt.customerId, staffId: appt.staffId,
        serviceIds: appt.services?.map((l) => l.serviceId) ?? [],
        appointmentDate: iso, startTime: `${minToTime(startMin)}:00`, endTime: `${minToTime(endMin)}:00`,
        status: appt.status, notes: appt.notes ?? null,
      });
      showToast(`Randevu ${minToTime(startMin)}'e taşındı.`, "info");
      loadCalendar();
    } catch (err) { showToast(err.response?.data?.error ?? "Taşıma başarısız.", "error"); }
  };

  // Boş hücreye tıklama → o slotun başlangıç/bitişini otomatik ata
  const clickEmptySlot = (e, iso) => {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let startMin = START_HOUR * 60 + Math.floor(((y / HOUR_PX) * 60) / SNAP_MIN) * SNAP_MIN;
    startMin = Math.max(START_HOUR * 60, Math.min(startMin, END_HOUR * 60 - 60));
    openCreate({ date: iso, start: minToTime(startMin), end: minToTime(startMin + 60) });
  };

  // --- Navigasyon ---
  const navigate = (dir) => {
    setAnchor((a) => {
      if (scale === "day") return addDays(a, dir);
      if (scale === "week") return addDays(a, dir * 7);
      return new Date(a.getFullYear(), a.getMonth() + dir, 1);
    });
  };
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); };

  const rangeLabel = useMemo(() => {
    if (scale === "day") return anchor.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" });
    if (scale === "month") return anchor.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
    return `${range.from.toLocaleDateString("tr-TR", { day: "numeric", month: "short" })} - ${range.to.toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}`;
  }, [scale, anchor, range]);

  // Akıllı tablo satırları: arama/filtre için düz alanlar (hizmet adları, durum etiketi)
  const listRows = useMemo(
    () => [...visibleAppointments]
      .sort((a, b) => (a.appointmentDate + a.startTime).localeCompare(b.appointmentDate + b.startTime))
      .map((a) => ({
        ...a,
        serviceNames: a.services?.map((l) => l.name).join(", ") ?? "",
        statusLabel: (STATUS_STYLES[a.status] || STATUS_STYLES.SCHEDULED).label,
      })),
    [visibleAppointments]
  );

  const listColumns = [
    { key: "appointmentDate", header: "Tarih", type: "date", sortable: true, render: (v) => new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) },
    { key: "startTime", header: "Saat", sortable: true, render: (v, row) => `${fmtTime(v)} - ${fmtTime(row.endTime)}` },
    {
      key: "customerName", header: "Müşteri", type: "string", sortable: true,
      render: (v, row) => (
        <div>
          <span className="font-semibold text-on-surface block">{v}</span>
          {row.customerPhone && <span className="text-[11px] text-on-surface-variant block">{row.customerPhone}</span>}
        </div>
      ),
    },
    {
      key: "staffName", header: "Personel", type: "string", sortable: true,
      render: (v, row) => (
        <span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${staffColor(row.staffId)}`}></span>{v}</span>
      ),
    },
    { key: "serviceNames", header: "Hizmetler", type: "string", sortable: false, render: (v) => <span className="text-on-surface-variant">{v || "—"}</span> },
    {
      key: "statusLabel", header: "Durum", type: "string", sortable: true,
      render: (v, row) => {
        const st = STATUS_STYLES[row.status] || STATUS_STYLES.SCHEDULED;
        return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${st.badge}`}>{st.label}</span>;
      },
    },
    { key: "totalPrice", header: "Tutar", type: "number", sortable: true, render: (v) => fmtMoney(v) },
  ];

  const listActions = (a) => (
    <div className="flex items-center justify-end gap-1.5">
      <button onClick={() => sendWhatsAppReminder(a)} title="WhatsApp ile hatırlat" className="p-1.5 rounded-lg text-success hover:bg-success/10 transition"><WhatsAppIcon className="text-[18px]" /></button>
      <button onClick={() => sendSmsReminder(a)} disabled={smsSendingId === a.id} title="SMS ile hatırlat" className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition disabled:opacity-40"><Icon name="sms" className="!text-[18px]" /></button>
      <button onClick={() => setSelected(a)} title="Detay" className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"><Icon name="visibility" className="!text-[18px]" /></button>
      <button onClick={() => openEdit(a)} title="Düzenle" className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"><Icon name="edit" className="!text-[18px]" /></button>
      <button onClick={() => triggerDelete(a)} disabled={isPaid(a)} title={isPaid(a) ? paidLockTitle : "Sil"} className="p-1.5 rounded-lg text-error hover:bg-error/10 transition disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"><Icon name="delete" className="!text-[18px]" /></button>
    </div>
  );

  // --- Zaman ızgarası (gün/hafta ortak) ---
  const renderTimeGrid = () => (
    <div className="min-w-[720px]" style={{ minWidth: scale === "day" ? 480 : 1000 }}>
      <div className="grid sticky top-0 bg-surface-card z-10 border-b border-outline-variant" style={{ gridTemplateColumns: `72px repeat(${timeDays.length}, 1fr)` }}>
        <div className="h-16 flex items-center justify-center border-r border-outline-variant">
          <Icon name="schedule" className="text-on-surface-variant" />
        </div>
        {timeDays.map((d, i) => {
          const isToday = toISO(d) === todayISO;
          return (
            <div key={i} className={`h-16 flex flex-col items-center justify-center border-r border-outline-variant ${isToday ? "bg-primary/5" : ""}`}>
              <span className={`text-[11px] uppercase ${isToday ? "text-primary font-bold" : "text-on-surface-variant"}`}>{DAY_LABELS[(d.getDay() + 6) % 7]}</span>
              <span className={`text-lg font-bold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</span>
            </div>
          );
        })}
      </div>
      <div className="grid relative" style={{ gridTemplateColumns: `72px repeat(${timeDays.length}, 1fr)` }}>
        <div className="border-r border-outline-variant text-right pr-3 text-[11px] font-medium text-on-surface-variant">
          {HOURS.map((h) => <div key={h} style={{ height: HOUR_PX }} className="pt-1">{String(h).padStart(2, "0")}:00</div>)}
        </div>
        {timeDays.map((d, i) => {
          const iso = toISO(d);
          const isToday = iso === todayISO;
          return (
            <div
              key={i}
              className={`relative border-r border-outline-variant/40 ${isToday ? "bg-primary/5" : ""}`}
              style={{ height: GRID_HEIGHT }}
              onClick={(e) => clickEmptySlot(e, iso)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => dropToSlot(e, iso)}
            >
              {HOURS.map((h) => <div key={h} style={{ height: HOUR_PX }} className="border-b border-outline-variant/25 pointer-events-none" />)}
              {apptsByDay(iso).map((a) => {
                const geo = cardGeometry(a);
                const st = STATUS_STYLES[a.status] || STATUS_STYLES.SCHEDULED;
                const svcNames = a.services?.map((l) => l.name).join(", ");
                return (
                  <div
                    key={a.id}
                    draggable={!isPaid(a)}
                    onDragStart={() => !isPaid(a) && setDragId(a.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={(e) => { e.stopPropagation(); setSelected(a); }}
                    className={`absolute left-1.5 right-1.5 ${st.card} border-l-4 rounded-lg p-2 hover:shadow-lg hover:-translate-y-0.5 transition-all z-20 overflow-hidden ${
                      isPaid(a) ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"
                    } ${dragId === a.id ? "opacity-40" : ""}`}
                    style={{ top: geo.top, height: geo.height }}
                    title={isPaid(a) ? `${a.customerName} — ${svcNames} (Tahsilat yapıldı - Taşınamaz)` : `${a.customerName} — ${svcNames}`}
                  >
                    <div className="flex justify-between items-start gap-1">
                      <p className={`font-bold text-[12px] truncate ${a.status === "CANCELLED" ? "line-through" : ""}`}>{a.customerName}</p>
                      <span className={`w-2 h-2 rounded-full shrink-0 mt-0.5 ${st.dot}`}></span>
                    </div>
                    <p className="text-[10px] text-on-surface-variant truncate">{svcNames}</p>
                    {geo.height > 58 && (
                      <p className="text-[10px] text-on-surface-variant mt-1 flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${staffColor(a.staffId)}`}></span>
                        {a.staffName} · {fmtTime(a.startTime)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  // --- Ay görünümü ---
  const renderMonth = () => {
    const weeks = [];
    for (let i = 0; i < gridDays.length; i += 7) weeks.push(gridDays.slice(i, i + 7));
    const curMonth = anchor.getMonth();
    return (
      <div className="p-4">
        <div className="grid grid-cols-7 gap-px bg-outline-variant/40 rounded-t-xl overflow-hidden">
          {DAY_LABELS.map((l) => <div key={l} className="bg-surface-card py-2 text-center text-[11px] font-bold uppercase text-on-surface-variant">{l}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-px bg-outline-variant/40 rounded-b-xl overflow-hidden">
          {gridDays.map((d, i) => {
            const iso = toISO(d);
            const dayAppts = apptsByDay(iso);
            const isToday = iso === todayISO;
            const inMonth = d.getMonth() === curMonth;
            return (
              <div
                key={i}
                onClick={() => openCreate({ date: iso })}
                className={`bg-surface-card min-h-[104px] p-1.5 cursor-pointer hover:bg-surface-container-low transition ${inMonth ? "" : "opacity-40"}`}
              >
                <div className={`text-xs font-bold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-primary text-white" : "text-on-surface"}`}>{d.getDate()}</div>
                <div className="space-y-1">
                  {dayAppts.slice(0, 3).map((a) => {
                    const st = STATUS_STYLES[a.status] || STATUS_STYLES.SCHEDULED;
                    return (
                      <div key={a.id} onClick={(e) => { e.stopPropagation(); setSelected(a); }}
                        className={`${st.card} border-l-2 rounded px-1.5 py-0.5 text-[10px] truncate hover:shadow`}
                        title={`${fmtTime(a.startTime)} ${a.customerName}`}>
                        <span className="font-semibold">{fmtTime(a.startTime)}</span> {a.customerName}
                      </div>
                    );
                  })}
                  {dayAppts.length > 3 && <div className="text-[10px] text-on-surface-variant pl-1">+{dayAppts.length - 3} daha</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- Liste görünümü (akıllı tablo: arama, sütun filtresi, sıralama, sayfalama, dışa aktar) ---
  const renderList = () => (
    <div className="p-4">
      <AdvancedTable
        columns={listColumns}
        data={listRows}
        actions={listActions}
        onRowClick={(a) => setSelected(a)}
        searchPlaceholder="Randevu ara (müşteri, personel, hizmet, durum)..."
      />
    </div>
  );

  const scaleBtn = (val, label) => (
    <button
      onClick={() => setScale(val)}
      className={`px-3 py-1.5 rounded-md text-sm transition ${scale === val ? "bg-surface-card shadow-sm text-primary font-bold" : "text-on-surface-variant hover:text-on-surface"}`}
    >{label}</button>
  );

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8 flex flex-col h-[calc(100vh-4rem)]">
      {/* Alt başlık / kontroller */}
      <div className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-surface-card border-b border-outline-variant">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="calendar_today" filled />
          </div>
          <h2 className="text-xl font-bold text-on-surface">Randevular</h2>
          <div className="flex items-center bg-surface-container rounded-lg p-1 ml-2">
            <button onClick={() => setViewMode("calendar")} className={`px-3 py-1.5 rounded-md text-sm transition ${viewMode === "calendar" ? "bg-surface-card shadow-sm text-primary font-bold" : "text-on-surface-variant"}`}>Takvim</button>
            <button onClick={() => setViewMode("list")} className={`px-3 py-1.5 rounded-md text-sm transition ${viewMode === "list" ? "bg-surface-card shadow-sm text-primary font-bold" : "text-on-surface-variant"}`}>Liste</button>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {viewMode === "calendar" && (
            <div className="flex items-center bg-surface-container rounded-lg p-1">
              {scaleBtn("day", "Gün")}{scaleBtn("week", "Hafta")}{scaleBtn("month", "Ay")}
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-2 bg-surface-card border border-outline-variant rounded-xl text-sm">
            <Icon name="calendar_today" className="!text-[18px] text-on-surface-variant" />
            <span className="font-medium capitalize">{rangeLabel}</span>
          </div>
          <div className="flex gap-1">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-surface-container-high rounded-lg transition"><Icon name="chevron_left" /></button>
            <button onClick={goToday} className="px-3 py-1 text-sm font-bold hover:bg-surface-container-high rounded-lg transition">Bugün</button>
            <button onClick={() => navigate(1)} className="p-2 hover:bg-surface-container-high rounded-lg transition"><Icon name="chevron_right" /></button>
          </div>
          <button onClick={() => openCreate()} className="bg-primary text-white px-5 py-2 rounded-full font-semibold text-sm hover:opacity-90 active:scale-95 transition shadow-md flex items-center gap-1.5">
            <Icon name="add" className="!text-[18px]" /> Yeni Randevu
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sol filtre */}
        <aside className="w-60 border-r border-outline-variant p-5 overflow-y-auto hidden lg:block bg-surface-card">
          <div className="space-y-7">
            <section>
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-on-surface-variant mb-3">Personel</h3>
              <div className="space-y-2.5">
                {staff.map((s) => (
                  <label key={s.id} className="flex items-center gap-2.5 cursor-pointer group">
                    <input type="checkbox" checked={isStaffChecked(s.id)} onChange={() => toggleStaff(s.id)} className="rounded text-primary focus:ring-primary border-outline-variant" />
                    <span className={`w-2 h-2 rounded-full ${staffColor(s.id)}`}></span>
                    <span className="text-sm text-on-surface group-hover:text-primary transition truncate">{s.fullName}</span>
                  </label>
                ))}
                {staff.length === 0 && <p className="text-xs text-on-surface-variant">Personel yok.</p>}
              </div>
            </section>
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">Hizmetler</h3>
                {serviceFilter && (
                  <button
                    onClick={clearServiceFilter}
                    className="text-[10px] text-primary hover:underline font-semibold transition"
                  >
                    Temizle
                  </button>
                )}
              </div>
              <div className="space-y-4 max-h-[320px] overflow-y-auto pr-1">
                {Object.entries(servicesByCategory).map(([category, svcs]) => (
                  <div key={category} className="space-y-1.5">
                    <div className="text-[10px] font-bold text-on-surface-variant/75 uppercase tracking-wide border-b border-outline-variant/30 pb-0.5 mb-1 bg-surface-container-low/30 px-1.5 py-0.5 rounded">
                      {category}
                    </div>
                    <div className="space-y-1.5 pl-1.5">
                      {svcs.map((s) => (
                        <label key={s.id} className="flex items-start gap-2.5 cursor-pointer group py-0.5">
                          <input
                            type="checkbox"
                            checked={isServiceFilterChecked(s.id)}
                            onChange={() => toggleServiceFilter(s.id)}
                            className="rounded text-primary focus:ring-primary border-outline-variant mt-0.5 w-3.5 h-3.5"
                          />
                          <span className="text-xs text-on-surface group-hover:text-primary transition leading-tight truncate" title={s.name}>
                            {s.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {services.length === 0 && <p className="text-xs text-on-surface-variant">Hizmet yok.</p>}
              </div>
            </section>
            <section className="pt-5 border-t border-outline-variant/40">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-on-surface-variant mb-3">Durum</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs"><span className="w-3 h-3 rounded-full bg-blue-500"></span> Planlandı</div>
                <div className="flex items-center gap-2 text-xs"><span className="w-3 h-3 rounded-full bg-success"></span> Tamamlandı</div>
                <div className="flex items-center gap-2 text-xs"><span className="w-3 h-3 rounded-full bg-error"></span> İptal</div>
              </div>
              {viewMode === "calendar" && scale !== "month" && (
                <p className="text-[11px] text-on-surface-variant mt-4 leading-relaxed">
                  <Icon name="drag_indicator" className="!text-[14px]" /> Kartı sürükleyip boş bir saate bırakarak taşıyabilirsiniz.
                </p>
              )}
            </section>
          </div>
        </aside>

        {/* İçerik */}
        <div className="flex-1 overflow-auto bg-surface">
          {loading ? (
            <div className="p-8 text-center text-on-surface-variant">Yükleniyor…</div>
          ) : viewMode === "list" ? renderList()
            : scale === "month" ? renderMonth()
            : renderTimeGrid()}
        </div>
      </div>

      {/* Detay çekmecesi */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/30 z-[55]" onClick={() => setSelected(null)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-[400px] bg-surface-card shadow-2xl z-[60] border-l border-outline-variant flex flex-col animate-slide-in">
            <div className="p-6 border-b border-outline-variant flex items-center justify-between">
              <h3 className="text-lg font-bold">Randevu Detayı</h3>
              <div className="flex items-center gap-1">
                <button onClick={() => sendWhatsAppReminder(selected)} title="WhatsApp ile saat hatırlat" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-success bg-success/10 hover:bg-success/20 font-semibold text-sm transition"><WhatsAppIcon className="text-[18px]" /> WhatsApp</button>
                <button type="button" disabled title="SMS gönderimi şu an devre dışı" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-on-surface-variant bg-surface-container-high font-semibold text-sm opacity-50 cursor-not-allowed"><Icon name="sms" className="!text-[18px]" /> SMS</button>
                <button onClick={() => setSelected(null)} className="p-2 hover:bg-surface-container-high rounded-full"><Icon name="close" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-xl font-bold">{initials(selected.customerName)}</div>
                <div>
                  <h4 className="text-lg font-bold">{selected.customerName}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${(STATUS_STYLES[selected.status] || STATUS_STYLES.SCHEDULED).badge}`}>{(STATUS_STYLES[selected.status] || STATUS_STYLES.SCHEDULED).label}</span>
                    <span className="text-xs text-on-surface-variant">{selected.customerPhone || ""}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-surface-container-low p-3 rounded-xl"><p className="text-[11px] text-on-surface-variant mb-1">Süre</p><p className="font-bold">{selected.durationMinutes} dk</p></div>
                <div className="bg-surface-container-low p-3 rounded-xl"><p className="text-[11px] text-on-surface-variant mb-1">Toplam Tutar</p><p className="font-bold">{fmtMoney(selected.totalPrice)}</p></div>
                
                {Number(selected.discountAmount) > 0 && (
                  <div className="bg-surface-container-low p-3 rounded-xl col-span-2 flex justify-between items-center bg-red-50/50 border border-red-100">
                    <span className="text-xs font-semibold text-red-700 flex items-center gap-1">
                      <Icon name="local_offer" className="!text-[14px]" /> Uygulanan İndirim / Kampanya
                    </span>
                    <span className="font-bold text-red-700">-{fmtMoney(selected.discountAmount)}</span>
                  </div>
                )}
                
                <div className="bg-surface-container-low p-3 rounded-xl"><p className="text-[11px] text-on-surface-variant mb-1">Tahsil Edilen</p><p className="font-bold text-success">{fmtMoney(selected.actualPaidAmount)}</p></div>
                <div className="bg-surface-container-low p-3 rounded-xl"><p className="text-[11px] text-on-surface-variant mb-1">Kalan Ödeme</p><p className={`font-bold ${Number(selected.remainingAmount) > 0 ? "text-error" : "text-success"}`}>{fmtMoney(selected.remainingAmount)}</p></div>
                <div className="bg-surface-container-low p-3 rounded-xl col-span-2"><p className="text-[11px] text-on-surface-variant mb-1">Tarih & Saat</p><p className="font-bold">{new Date(selected.appointmentDate).toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}, {fmtTime(selected.startTime)} - {fmtTime(selected.endTime)}</p></div>
              </div>
              <div>
                <h5 className="font-bold mb-2 text-xs uppercase tracking-wider text-on-surface-variant">Hizmetler</h5>
                <div className="space-y-1.5">
                  {selected.services?.map((l) => (
                    <div key={l.serviceId} className="flex justify-between bg-surface-container-low rounded-lg px-3 py-2">
                      <span>{l.name} <span className="text-on-surface-variant">· {l.durationMinutes} dk</span></span>
                      <span className="font-semibold">{fmtMoney(l.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 border border-outline-variant rounded-xl flex items-center gap-3">
                <span className={`w-3 h-3 rounded-full ${staffColor(selected.staffId)}`}></span>
                <div><p className="text-[11px] text-on-surface-variant">Atanan Personel</p><p className="font-bold">{selected.staffName}</p></div>
              </div>
              {selected.notes && (
                <div>
                  <h5 className="font-bold mb-2 text-xs uppercase tracking-wider text-on-surface-variant">Not</h5>
                  <div className="p-4 bg-surface-container-highest/30 rounded-xl italic text-on-surface-variant whitespace-pre-wrap">{selected.notes}</div>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-outline-variant bg-surface-container-low space-y-2">
              <button onClick={() => goToPayment(selected)} className="w-full py-2.5 bg-success text-white font-semibold rounded-xl hover:opacity-90 shadow-lg transition text-sm flex items-center justify-center gap-1.5"><Icon name="payments" className="!text-[18px]" /> Ödeme Al</button>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => openEdit(selected)} className="py-2.5 border border-outline-variant rounded-xl font-semibold text-sm hover:bg-surface-container-high transition flex items-center justify-center gap-1.5"><Icon name="edit" className="!text-[18px]" /> Düzenle</button>
                <button onClick={() => triggerDelete(selected)} disabled={isPaid(selected)} title={isPaid(selected) ? paidLockTitle : undefined} className="py-2.5 border border-error/30 text-error rounded-xl font-semibold text-sm hover:bg-error/10 transition flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"><Icon name="delete" className="!text-[18px]" /> Sil</button>
              </div>
              {isPaid(selected) && (
                <p className="text-[11px] text-on-surface-variant text-center">Tahsilat yapıldığı için bu randevu silinemez veya iptal edilemez.</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {selected.status !== "CANCELLED" && <button onClick={() => changeStatus(selected, "CANCELLED")} disabled={isPaid(selected)} title={isPaid(selected) ? paidLockTitle : undefined} className="py-2.5 border border-outline text-on-surface font-semibold rounded-xl hover:bg-surface-container-high transition text-sm disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed">İptal Et</button>}
                {selected.status !== "COMPLETED" && <button onClick={() => changeStatus(selected, "COMPLETED")} className="py-2.5 bg-primary text-white font-semibold rounded-xl hover:opacity-90 shadow-lg transition text-sm">Tamamlandı</button>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Yeni/Düzenle randevu modalı */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-surface-card w-full max-w-lg rounded-2xl shadow-modal overflow-hidden max-h-[92vh] flex flex-col">
            <div className="p-5 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <h3 className="text-lg font-bold">{editingId ? "Randevuyu Düzenle" : "Yeni Randevu"}</h3>
              <button onClick={closeModal} className="p-2 hover:bg-surface-container-high rounded-full"><Icon name="close" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5"><Icon name="error" className="!text-[16px]" /> {formError}</div>
              )}
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant flex items-center justify-between">
                  <span>Müşteri (Opsiyonel)</span>
                  <span className="text-[10px] text-on-surface-variant/70 font-normal">Boş kalabilir, sonradan seçilebilir</span>
                </label>
                <AsyncCustomerSelect
                  value={form.customerId}
                  initialLabel={form.customerLabel}
                  onChange={(id, c, typedQuery) =>
                    setForm((f) => ({
                      ...f,
                      customerId: id,
                      customerLabel: c?.fullName ?? typedQuery ?? "",
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Personel *</label>
                <select required value={form.staffId} onChange={(e) => setForm({ ...form, staffId: e.target.value })} className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary">
                  <option value="">Personel seçin…</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.fullName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Hizmet Seçimi * (Açılır Menü)</label>
                
                {/* Combobox Dropdown */}
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      toggleService(e.target.value);
                    }
                  }}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary text-sm cursor-pointer mb-2"
                >
                  <option value="">+ Hizmet eklemek için seçin…</option>
                  {Object.entries(servicesByCategory).map(([category, svcs]) => (
                    <optgroup key={category} label={category}>
                      {svcs.map((s) => {
                        const isSelected = form.serviceIds.includes(s.id);
                        return (
                          <option key={s.id} value={s.id} disabled={isSelected}>
                            {isSelected ? "✓ " : ""}{s.name} ({s.durationMinutes || 60} dk - {fmtMoney(s.price)})
                          </option>
                        );
                      })}
                    </optgroup>
                  ))}
                </select>

                {/* Selected Services Chips / List */}
                {form.serviceIds.length > 0 ? (
                  <div className="space-y-2 bg-surface-container-low p-3 rounded-xl border border-outline-variant/40">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-on-surface-variant uppercase tracking-wider">
                        Seçilen Hizmetler ({form.serviceIds.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, serviceIds: [] }))}
                        className="text-[11px] text-rose-600 hover:underline font-medium cursor-pointer"
                      >
                        Tümünü Temizle
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {form.serviceIds.map((id) => {
                        const s = services.find((x) => x.id === id);
                        if (!s) return null;
                        return (
                          <div
                            key={id}
                            className="inline-flex items-center gap-2 bg-primary/10 text-primary border border-primary/20 px-3 py-1.5 rounded-lg text-xs font-medium group transition"
                          >
                            <span>{s.name}</span>
                            <span className="text-[10px] text-primary/70 font-mono">
                              ({s.durationMinutes || 60} dk · {fmtMoney(s.price)})
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleService(id)}
                              className="hover:bg-primary/20 p-0.5 rounded-full text-primary transition cursor-pointer"
                              title="Hizmeti Kaldır"
                            >
                              <Icon name="close" className="!text-[14px]" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-2 border-t border-outline-variant/30 flex items-center justify-between text-xs font-semibold text-on-surface">
                      <span>Toplam Süre & Tutar:</span>
                      <span className="text-primary font-bold">
                        {selectedDuration} dk · {fmtMoney(selectedTotal)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-on-surface-variant italic px-1">
                    Henüz hizmet seçilmedi. Yukarıdaki açılır menüden randevu hizmeti ekleyebilirsiniz.
                  </p>
                )}
                {willRevertToScheduled && (
                  <p className="mt-2 flex items-start gap-1.5 text-[11px] text-tier-gold bg-tier-gold/10 border border-tier-gold/25 rounded-lg px-2.5 py-2">
                    <Icon name="info" className="!text-[14px] mt-px shrink-0" />
                    <span>
                      Tamamlanmış randevuya yeni hizmet eklediniz. Kaydedince randevu{" "}
                      <strong>Planlandı</strong> durumuna dönecek — böylece yeni hizmetin tahsilatı
                      gözden kaçmaz. Tahsilat alındıktan sonra tekrar Tamamlandı yapabilirsiniz.
                    </span>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Tarih *</label>
                  <input type="date" required value={form.appointmentDate} onChange={(e) => setForm({ ...form, appointmentDate: e.target.value })} className="w-full bg-white border border-outline-variant rounded-xl px-2 py-2.5 outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlangıç *</label>
                  <input type="time" required value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="w-full bg-white border border-outline-variant rounded-xl px-2 py-2.5 outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Bitiş</label>
                  <input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="w-full bg-white border border-outline-variant rounded-xl px-2 py-2.5 outline-none focus:border-primary" />
                </div>
              </div>
              <p className="text-[11px] text-on-surface-variant -mt-2">Bitiş boş bırakılırsa hizmet sürelerinden otomatik hesaplanır.</p>
              {editingId && (
                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Durum</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary">
                    <option value="SCHEDULED">Planlandı</option>
                    <option value="COMPLETED">Tamamlandı</option>
                    <option value="CANCELLED">İptal</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Not</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary resize-none" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeModal} className="px-5 py-2.5 font-semibold text-on-surface-variant hover:bg-surface-container-high rounded-xl transition text-sm">Vazgeç</button>
                <button type="submit" disabled={saving} className="px-8 py-2.5 bg-primary text-white font-semibold rounded-xl shadow-lg hover:opacity-90 transition disabled:opacity-50 text-sm">{saving ? "Kaydediliyor…" : editingId ? "Güncelle" : "Randevu Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />

      {paymentRow && (
        <PaymentCollectModal
          row={paymentRow}
          onClose={() => setPaymentRow(null)}
          onSaved={() => { setPaymentRow(null); loadCalendar(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}
